import React, { useEffect, useMemo, useState } from "react";
import { exportOrder } from "@/lib/exportOrder";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Search, Download, FileText, Send, Loader2, ChevronDown, Target, History, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAVY = "#0F2C59";
const GREEN = "#16A34A";
const AMBER = "#F59E0B";

const PREDS = [
  ["KESIN", "Kesin"], ["BELKI", "Belki"], ["KARSI", "Karşı"],
  ["ULASILAMADI", "Ulaşılamadı"], ["GELMEYECEK", "Gelmeyecek"], ["BOS", "İşaretsiz"],
];
const PRED_LABEL = Object.fromEntries(PREDS);

const MultiPicker = ({ label, options, value, onChange, testid, width = "w-56" }) => {
  const all = value.length === 0;
  const toggle = (k) => onChange(value.includes(k) ? value.filter((v) => v !== k) : [...value, k]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`h-9 ${width} justify-between font-normal`} data-testid={testid}>
          <span className="truncate text-xs">
            {label}: {all ? "Tümü" : `${value.length} seçili`}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 max-h-80 overflow-y-auto p-2" align="start">
        <button className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted font-medium"
          onClick={() => onChange([])} data-testid={`${testid}-all`}>
          Tümü
        </button>
        <div className="mt-1 space-y-0.5">
          {options.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-xs">
              <Checkbox checked={value.includes(k)} onCheckedChange={() => toggle(k)}
                data-testid={`${testid}-opt-${k}`} />
              <span className="truncate">{l}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const BranchSupport = () => {
  const { user } = useAuth();
  const showContact = !!user?.is_owner;
  const [meta, setMeta] = useState(null);
  const [coms, setComs] = useState([]);
  const [branch, setBranch] = useState("all");
  const [centralPreds, setCentralPreds] = useState([]);
  const [branchPreds, setBranchPreds] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [reqs, setReqs] = useState([]);
  const [openReq, setOpenReq] = useState(null);

  const loadRequests = () => {
    api.get("/reports/branch-support/requests")
      .then(({ data }) => setReqs(data.requests || []))
      .catch(() => {});
  };
  useEffect(loadRequests, []);

  useEffect(() => {
    api.get("/reports/branch-support", { params: { committees: "0" } })
      .then(({ data }) => setMeta(data))
      .catch(() => toast.error("Filtreler yüklenemedi"));
  }, []);

  const params = useMemo(() => {
    const p = {};
    if (coms.length) p.committees = coms.join(",");
    if (branch !== "all") p.branch = branch;
    if (centralPreds.length) p.central_preds = centralPreds.join(",");
    if (branchPreds.length) p.branch_preds = branchPreds.join(",");
    return p;
  }, [coms, branch, centralPreds, branchPreds]);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/branch-support", { params });
      setData(data);
      setSelected(new Set((data.rows || []).map((r) => r.voter_id)));
      if (!data.total) toast.info("Filtrelere uyan firma bulunamadı");
    } catch {
      toast.error("Liste getirilemedi");
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    try {
      const order = await exportOrder(bsuT.sorted.map((r) => r.voter_id));
      const res = await api.get("/reports/branch-support/export", {
        params: { ...params, format, order }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sube_destek.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarılamadı");
    }
  };

  const send = async () => {
    if (branch === "all") { toast.error("Göndermek için tek bir şube seçin"); return; }
    const ids = rows.map((r) => r.voter_id).filter((id) => selected.has(id));
    if (!ids.length) { toast.error("Gönderilecek firma seçilmedi"); return; }
    setSending(true);
    try {
      const { data: res } = await api.post("/reports/branch-support/send", {
        branch, voter_ids: ids,
        note: "Kritik komiteler için merkez destek talebi",
      });
      toast.success(`${res.sent_to.join(", ")} kişisine ${res.count} firma gönderildi`);
      loadRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLocaleLowerCase("tr");
    if (!term) return data.rows;
    return data.rows.filter((r) => (r.company_name || "").toLocaleLowerCase("tr").includes(term)
      || (r.contact_person || "").toLocaleLowerCase("tr").includes(term)
      || String(r.sicil_no || "").includes(term));
  }, [data, q]);
  const bsuT = useTableSort(rows);
  const reqT = useTableSort([]);

  const selectedCount = useMemo(() => rows.filter((r) => selected.has(r.voter_id)).length, [rows, selected]);
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const toggleRow = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.voter_id)));

  const comOptions = useMemo(    () => (meta?.committees || []).map((c) => [String(c.committee_no), `${c.committee_no}. ${c.name}`]),
    [meta]
  );

  if (!meta) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-4" data-testid="branch-support">
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4" style={{ color: AMBER }} />
          <p className="text-sm font-semibold" style={{ color: NAVY }}>Kritik komiteler için şube desteği</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <MultiPicker label="Komite" options={comOptions} value={coms} onChange={setComs}
            testid="bs-committees" width="w-64" />
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger className="h-9 w-48 text-xs" data-testid="bs-branch"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Şube: Tümü</SelectItem>
              {(meta.branches || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <MultiPicker label="Merkez 2026" options={PREDS} value={centralPreds} onChange={setCentralPreds}
            testid="bs-central" />
          <MultiPicker label="Şube 2026" options={PREDS} value={branchPreds} onChange={setBranchPreds}
            testid="bs-branchpred" />
          <Button onClick={run} disabled={loading} className="h-9 bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="bs-run">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Listele"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Merkez ve şube 2026 tahminlerini ayrı ayrı seçin; boş bırakırsanız tümü gelir. Yalnız FAAL firmalar listelenir.
        </p>
      </Card>

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className="text-xs" data-testid="bs-total">{data.total} firma</Badge>
            <Badge className="text-xs bg-[#16A34A] hover:bg-[#16A34A]" data-testid="bs-selected">{selectedCount} seçili</Badge>
            {(data.by_branch || []).slice(0, 6).map((b) => (
              <span key={b.branch} className="text-xs text-muted-foreground">{b.branch}: <b className="text-foreground">{b.count}</b></span>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="bs-export-xlsx">
                <Download className="h-3.5 w-3.5 mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="bs-export-pdf">
                <FileText className="h-3.5 w-3.5 mr-1" /> PDF
              </Button>
              <Button size="sm" onClick={send} disabled={sending || branch === "all"}
                className="bg-[#16A34A] hover:bg-[#16A34A]/90" data-testid="bs-send">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> {selectedCount} firma gönder</>}
              </Button>
            </div>
          </div>

          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Firma, yetkili veya sicil ara..." value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="bs-search" />
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto max-h-[32rem]">
              <table className="w-full text-xs" data-testid="bs-table">
                <thead className="bg-muted/60 text-muted-foreground sticky top-0">
                  <tr>
                    <th className="py-2 px-3 w-8">
                      <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="bs-select-all" />
                    </th>
                    <bsuT.Th sortKey="bsu-komite" get={(r) => r.committee_no} className="text-left font-medium py-2 px-3">Komite</bsuT.Th>
                    <bsuT.Th sortKey="bsu-firma" get={(r) => r.company_name} text className="text-left font-medium py-2 px-3">Firma</bsuT.Th>
                    <bsuT.Th sortKey="bsu-sube" get={(r) => r.branch} text className="text-left font-medium py-2 px-3">Şube</bsuT.Th>
                    <bsuT.Th sortKey="bsu-yetkili" get={(r) => r.contact_person || r.partner_info} text className="text-left font-medium py-2 px-3">Yetkili/Ortak</bsuT.Th>
                    {showContact && <bsuT.Th sortKey="bsu-tel" get={(r) => r.phone} text className="text-left font-medium py-2 px-3">Telefon</bsuT.Th>}
                    <bsuT.Th sortKey="bsu-merkez" get={(r) => r.center_prediction || ""} text className="text-center font-medium py-2 px-3">Merkez</bsuT.Th>
                    <bsuT.Th sortKey="bsu-subeegilim" get={(r) => r.branch_prediction || ""} text className="text-center font-medium py-2 px-3">Şube</bsuT.Th>
                  </tr>
                </thead>
                <tbody>
                  {bsuT.sorted.map((r) => (
                    <tr key={r.voter_id} className="border-t border-border/60 hover:bg-muted/30"
                      data-testid={`bs-row-${r.voter_id}`}>
                      <td className="py-2 px-3">
                        <Checkbox checked={selected.has(r.voter_id)} onCheckedChange={() => toggleRow(r.voter_id)}
                          data-testid={`bs-check-${r.voter_id}`} />
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap text-muted-foreground">{r.committee_no != null ? `#${r.committee_no}` : "—"}</td>
                      <td className="py-2 px-3 font-medium">{r.company_name}</td>
                      <td className="py-2 px-3">{r.branch}</td>
                      <td className="py-2 px-3 text-muted-foreground">{r.contact_person || "—"}</td>
                      {showContact && <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{r.phone || "—"}</td>}
                      <td className="py-2 px-3 text-center">{PRED_LABEL[r.central_prediction] || "İşaretsiz"}</td>
                      <td className="py-2 px-3 text-center" style={{ color: r.branch_prediction === "KESIN" ? GREEN : undefined }}>
                        {PRED_LABEL[r.branch_prediction] || "İşaretsiz"}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">Kayıt bulunamadı</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <p className="text-xs text-muted-foreground">
            "Şube başkanına gönder", seçili şubenin şube yöneticilerine firma listesiyle birlikte görev ve sistem bildirimi oluşturur.
          </p>
        </>
      )}

      <Card className="p-4 space-y-3" data-testid="bs-tracking">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4" style={{ color: NAVY }} />
          <p className="text-sm font-semibold" style={{ color: NAVY }}>Destek Takibi</p>
          <Badge variant="outline" className="text-xs">{reqs.length} talep</Badge>
        </div>
        {reqs.length === 0 ? (
          <p className="text-xs text-muted-foreground">Henüz şubeye gönderilmiş destek talebi yok.</p>
        ) : (
          <div className="space-y-2">
            {reqs.map((r) => (
              <div key={r.id} className="rounded-lg border border-border/70">
                <button className="w-full flex flex-wrap items-center gap-3 px-3 py-2 text-left hover:bg-muted/40"
                  onClick={() => setOpenReq(openReq === r.id ? null : r.id)} data-testid={`bs-req-${r.id}`}>
                  <span className="text-sm font-medium">{r.branch}</span>
                  <span className="text-xs text-muted-foreground">
                    Komite {r.committee_nos?.join(", ") || "—"} · {new Date(r.created_at).toLocaleDateString("tr-TR")}
                  </span>
                  <span className="text-xs">Gönderilen <b>{r.count}</b></span>
                  <span className="text-xs" style={{ color: GREEN }}>Kesine dönen <b>{r.new_kesin}</b></span>
                  <span className="text-xs">Şu an kesin <b>{r.kesin_now}</b></span>
                  <span className="text-xs">Yeni belge <b>{r.new_proxy}</b></span>
                  <span className="ml-auto text-xs font-semibold" style={{ color: r.conversion > 0 ? GREEN : undefined }}>
                    Dönüşüm %{r.conversion}
                  </span>
                  <ChevronDown className={`h-3.5 w-3.5 opacity-60 transition-transform ${openReq === r.id ? "rotate-180" : ""}`} />
                </button>
                {openReq === r.id && (
                  <div className="border-t border-border/60 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40 text-muted-foreground">
                        <tr>
                          <reqT.Th sortKey="rq-firma" get={(r) => r.company_name} text className="text-left font-medium py-1.5 px-3">Firma</reqT.Th>
                          <reqT.Th sortKey="rq-komite" get={(r) => r.committee_no ?? 0} className="text-left font-medium py-1.5 px-3">Komite</reqT.Th>
                          <reqT.Th sortKey="rq-gonderimde" get={(r) => r.prediction_at_send || ""} text className="text-center font-medium py-1.5 px-3">Gönderimde</reqT.Th>
                          <reqT.Th sortKey="rq-simdi" get={(r) => r.prediction || ""} text className="text-center font-medium py-1.5 px-3">Şimdi</reqT.Th>
                          <reqT.Th sortKey="rq-sonuc" get={(r) => (r.became_kesin ? 1 : 0)} className="text-center font-medium py-1.5 px-3">Sonuç</reqT.Th>
                        </tr>
                      </thead>
                      <tbody>
                        {reqT.applySort(r.items).map((it) => (
                          <tr key={it.voter_id} className="border-t border-border/40"
                            style={{ background: it.became_kesin ? "rgba(22,163,74,0.08)" : undefined }}>
                            <td className="py-1.5 px-3">{it.company_name}</td>
                            <td className="py-1.5 px-3 text-muted-foreground">#{it.committee_no ?? "—"}</td>
                            <td className="py-1.5 px-3 text-center text-muted-foreground">{PRED_LABEL[it.prediction_at_send] || "İşaretsiz"}</td>
                            <td className="py-1.5 px-3 text-center">{PRED_LABEL[it.current_prediction] || "—"}</td>
                            <td className="py-1.5 px-3 text-center">
                              {it.became_kesin ? (
                                <span className="inline-flex items-center gap-1 font-medium" style={{ color: GREEN }}>
                                  <CheckCircle2 className="h-3.5 w-3.5" /> Kesine döndü
                                </span>
                              ) : it.gained_proxy ? (
                                <span style={{ color: GREEN }}>Belge alındı</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
